import MuiSelect from '@mui/material/Select';
import { forwardRef } from 'react';
import type { ReactElement, Ref } from 'react';
import type { SelectProps } from '@mui/material/Select';
import { toVendor } from '../styles';
import type { Styleable } from '../styles';
import { SIZE } from '../wrap';
import type { ControlSize, Tone } from '../wrap';

/**
 * Written out rather than produced by `wrap`, because the type parameter is load
 * bearing: it is what makes `value` and the change handler agree. The factory
 * would flatten it to `unknown` and move the failure to every caller.
 */

export type SelectOwnProps<T> =
  & Omit<SelectProps<T>, 'sx' | 'size' | 'color' | 'component'>
  & Styleable
  & { size?: ControlSize; tone?: Tone };

/**
 * `forwardRef` erases the type parameter, so the component is built once and
 * then given its generic call signature back. Both halves are needed: the ref
 * because a Tooltip or a Popper has to attach to the field, the generic because
 * the callers rely on `value` and the change handler agreeing.
 */
const SelectBase = forwardRef(function Select(
  { size, tone, styles, vendorStyles, ...rest }: SelectOwnProps<unknown>,
  ref: Ref<unknown>,
) {
  return (
    <MuiSelect
      ref={ref}
      {...({
        ...rest,
        ...(size ? { size: SIZE[size] } : {}),
        ...(tone ? { color: tone } : {}),
        sx: toVendor(styles, vendorStyles),
      } as SelectProps<unknown>)}
    />
  );
});

export const Select = SelectBase as unknown as <T>(
  props: SelectOwnProps<T> & { ref?: Ref<unknown> },
) => ReactElement | null;
